import React, { useEffect, useState } from 'react';
import { Arzt, Praxis, Zeitslot, ZuweisungFilter as FilterType } from '../../types';
import { arztService } from '../../services/arzt.service';
import { praxisService } from '../../services/praxis.service';

interface ZuweisungFilterProps {
  onFilterChange: (filter: FilterType) => void;
}

const ZuweisungFilter: React.FC<ZuweisungFilterProps> = ({ onFilterChange }) => {
  const [aerzte, setAerzte] = useState<Arzt[]>([]);
  const [praxen, setPraxen] = useState<Praxis[]>([]);
  const [selectedArztId, setSelectedArztId] = useState<string>('');
  const [selectedPraxisId, setSelectedPraxisId] = useState<string>('');
  const [selectedZeitslot, setSelectedZeitslot] = useState<string>('');
  const [startDatum, setStartDatum] = useState<string>('');
  const [endDatum, setEndDatum] = useState<string>('');

  useEffect(() => {
    fetchAerzte();
    fetchPraxen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedArztId, selectedPraxisId, selectedZeitslot, startDatum, endDatum]);

  const fetchAerzte = async () => {
    try {
      const data = await arztService.getAllAerzte();
      setAerzte(data);
    } catch (err) {
      console.error('Fehler beim Laden der Ärzte:', err);
    }
  };

  const fetchPraxen = async () => {
    try {
      const data = await praxisService.getAllPraxen();
      setPraxen(data);
    } catch (err) {
      console.error('Fehler beim Laden der Praxen:', err);
    }
  };

  const applyFilters = () => {
    const filter: FilterType = {};

    if (selectedArztId) {
      filter.arztId = parseInt(selectedArztId);
    }

    if (selectedPraxisId) {
      filter.praxisId = parseInt(selectedPraxisId);
    }

    if (selectedZeitslot) {
      filter.zeitslot = selectedZeitslot as Zeitslot;
    }

    if (startDatum) {
      filter.startDatum = new Date(startDatum);
    }

    if (endDatum) {
      filter.endDatum = new Date(endDatum);
    }

    onFilterChange(filter);
  };

  const handleReset = () => {
    setSelectedArztId('');
    setSelectedPraxisId('');
    setSelectedZeitslot('');
    setStartDatum('');
    setEndDatum('');
  };

  return (
    <div className="bg-white shadow-md rounded-lg p-4 mb-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">Filter</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {/* Arzt Filter */}
        <div>
          <label htmlFor="arzt-filter" className="block text-sm font-medium text-gray-700 mb-1">
            Arzt
          </label>
          <select
            id="arzt-filter"
            value={selectedArztId}
            onChange={(e) => setSelectedArztId(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Alle Ärzte</option>
            {aerzte.map((arzt) => (
              <option key={arzt.id} value={arzt.id}>
                {arzt.vorname} {arzt.nachname}
              </option>
            ))}
          </select>
        </div>

        {/* Praxis Filter */}
        <div>
          <label htmlFor="praxis-filter" className="block text-sm font-medium text-gray-700 mb-1">
            Praxis
          </label>
          <select
            id="praxis-filter"
            value={selectedPraxisId}
            onChange={(e) => setSelectedPraxisId(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Alle Praxen</option>
            {praxen.map((praxis) => (
              <option key={praxis.id} value={praxis.id}>
                {praxis.name}
              </option>
            ))}
          </select>
        </div>

        {/* Zeitslot Filter */}
        <div>
          <label htmlFor="zeitslot-filter" className="block text-sm font-medium text-gray-700 mb-1">
            Zeitslot
          </label>
          <select
            id="zeitslot-filter"
            value={selectedZeitslot}
            onChange={(e) => setSelectedZeitslot(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Alle</option>
            <option value={Zeitslot.VORMITTAG}>Vormittag</option>
            <option value={Zeitslot.NACHMITTAG}>Nachmittag</option>
          </select>
        </div>

        {/* Start Date Filter */}
        <div>
          <label htmlFor="start-date-filter" className="block text-sm font-medium text-gray-700 mb-1">
            Von Datum
          </label>
          <input
            type="date"
            id="start-date-filter"
            value={startDatum}
            onChange={(e) => setStartDatum(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* End Date Filter */}
        <div>
          <label htmlFor="end-date-filter" className="block text-sm font-medium text-gray-700 mb-1">
            Bis Datum
          </label>
          <input
            type="date"
            id="end-date-filter"
            value={endDatum}
            onChange={(e) => setEndDatum(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Reset Button */}
      <div className="mt-4">
        <button
          onClick={handleReset}
          className="px-4 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
        >
          Filter zurücksetzen
        </button>
      </div>
    </div>
  );
};

export default ZuweisungFilter;
